import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Prediction } from "@/types";
import { PUBLIC_PREDICTION_COLUMNS, PREDICTIONS_READ } from "@/lib/predictionColumns";

export type ClusterKind = "league" | "market" | "team";

interface ClusterPredictionsArgs {
  kind: ClusterKind;
  matchers: string[]; // substrings to match against league/market/team columns
  page: number;
  pageSize: number;
  upcomingOnly?: boolean;
}

/**
 * Fetches approved predictions for a topical cluster (league / market / team)
 * with server-side pagination and an exact count.
 */
export function useClusterPredictions({
  kind,
  matchers,
  page,
  pageSize,
  upcomingOnly = true,
}: ClusterPredictionsArgs) {
  return useQuery({
    queryKey: ["cluster-predictions", kind, matchers, page, pageSize, upcomingOnly],
    placeholderData: keepPreviousData,
    enabled: matchers.length > 0,
    queryFn: async () => {
      let q = supabase
        .from(PREDICTIONS_READ)
        .select(PUBLIC_PREDICTION_COLUMNS, { count: "exact" })
        .eq("status", "approved");

      if (upcomingOnly) {
        q = q.gte("kickoff_time", new Date().toISOString());
      }

      // Build an OR of ilike conditions against the relevant column(s).
      const columns =
        kind === "league" ? ["league"] : kind === "market" ? ["market_type"] : ["team_home", "team_away"];

      const orParts: string[] = [];
      for (const col of columns) {
        for (const m of matchers) {
          orParts.push(`${col}.ilike.%${m}%`);
        }
      }
      if (orParts.length > 0) {
        q = q.or(orParts.join(","));
      }

      q = q.order("kickoff_time", { ascending: true });

      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error, count } = await q.range(from, to);
      if (error) throw error;
      return { rows: ((data as unknown) as Prediction[]) || [], count: count ?? 0 };
    },
  });
}
